import { BatteryStatus, SignalQuality } from '@/api_generated/api'
import { normalizeSignalQuality, normalizeBatteryStatus } from '@/core/models/sensorHealth'

const FILLED_COLOR = '#3D3D3D'
const UNFILLED_COLOR = '#BEBEBE'

interface HealthIconProps {
    size?: number
}

// Horizontal battery whose fill reflects the battery status bucket:
// Good -> full, Low -> one third, Empty/N/A -> empty outline.
export const BatteryLevelIcon = ({ status, size = 24 }: HealthIconProps & { status?: BatteryStatus | null }) => {
    const normalized = normalizeBatteryStatus(status)
    const fillFraction = normalized === BatteryStatus.Good ? 1 : normalized === BatteryStatus.Low ? 1 / 3 : 1
    const outlineColor = normalized !== BatteryStatus.NotAvailable ? FILLED_COLOR : FILLED_COLOR

    const innerX = 3.5
    const innerMaxWidth = 12
    const fillWidth = innerMaxWidth * fillFraction

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" role="img" aria-label={`Battery ${normalized}`}>
            <rect x="2" y="8" width="16.5" height="8" rx="2" stroke={outlineColor} strokeWidth="1.5" />
            <rect x="19.5" y="10.5" width="1.8" height="3" rx="0.6" fill={outlineColor} />
            {fillWidth > 0 && <rect x={innerX} y="9.75" width={fillWidth} height="4.5" rx="1" fill={FILLED_COLOR} />}
        </svg>
    )
}

// Wifi fan whose filled parts reflect the signal quality bucket:
// Good -> dot + both arcs, OK -> dot + inner arc, Bad -> dot only, N/A -> all muted.
// Unfilled parts keep a light, visible border.
export const SignalQualityIcon = ({ quality, size = 24 }: HealthIconProps & { quality?: SignalQuality | null }) => {
    const normalized = normalizeSignalQuality(quality)
    const level =
        normalized === SignalQuality.Good
            ? 3
            : normalized === SignalQuality.OK
              ? 2
              : normalized === SignalQuality.Bad
                ? 1
                : 3

    const dotColor = level >= 1 ? FILLED_COLOR : UNFILLED_COLOR
    const innerArcColor = level >= 2 ? FILLED_COLOR : UNFILLED_COLOR
    const outerArcColor = level >= 3 ? FILLED_COLOR : UNFILLED_COLOR

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" role="img" aria-label={`Signal ${normalized}`}>
            <path d="M4.2 12.5 A9 9 0 0 1 19.8 12.5" stroke={outerArcColor} strokeWidth="2" strokeLinecap="round" />
            <path d="M7.7 14.5 A5 5 0 0 1 16.3 14.5" stroke={innerArcColor} strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="17" r="1.7" fill={dotColor} />
        </svg>
    )
}
